import { useState, useEffect } from "react";
import { Modal, Button, Form, Badge, ProgressBar } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import ApiHelper from "../../../Helpers/ApiHelper";

// analysisType: "inventory" for personality inventory, "test" for skill/language tests
export default function AiResponseModal({ show, onHide, autoUploadFile, analysisType = "inventory" }) {
  const { t } = useTranslation();
  const user = useSelector((state) => state.auth.user);

  const [selectedFile, setSelectedFile] = useState(null);
  const [additionalContext, setAdditionalContext] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [aiResponse, setAiResponse] = useState(null);
  const [error, setError] = useState(null);

  // Handle auto-upload when file is provided - just set the file, don't auto-submit
  useEffect(() => {
    if (show && autoUploadFile && !aiResponse && !isLoading) {
      setSelectedFile(autoUploadFile);
    }
  }, [show, autoUploadFile, aiResponse, isLoading]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type !== "application/pdf") {
        setError(t("onlyPdfAllowed"));
        setSelectedFile(null);
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        setError(t("fileTooLarge"));
        setSelectedFile(null);
        return;
      }
      setError(null);
      setSelectedFile(file);
    }
  };

  const handleSubmit = () => {
    if (!selectedFile) {
      setError(t("pleaseSelectPdf"));
      return;
    }

    setIsLoading(true);
    setError(null);

    const formData = new FormData();
    formData.append("PdfFile", selectedFile);
    if (additionalContext.trim()) {
      formData.append("AdditionalContext", additionalContext.trim());
    }

    // Choose endpoint based on analysisType
    const endpoint = analysisType === "test"
      ? "ai/AnalyzeTestResults"
      : "ai/AnalyzeCandidatePdf";

    ApiHelper.xmlPostFormData(
      endpoint,
      formData,
      user.token,
      function (response) {
        setIsLoading(false);
        try {
          // Check if HTTP request was successful
          if (response.status !== 200) {
            setError(t("aiAnalysisError") + ` (HTTP ${response.status})`);
            return;
          }
          const result = JSON.parse(response.responseText);
          // statusCode: 1 means success, statusCode: 0 means error
          if (result.statusCode === 1 && result.data) {
            setAiResponse(result.data);
          } else {
            setError(result.message || t("aiAnalysisError"));
          }
        } catch (e) {
          console.error("Error parsing AI response:", e);
          setError(t("aiAnalysisError"));
        }
      },
      function (error) {
        setIsLoading(false);
        console.error("AI Analysis request failed:", error);
        setError(t("aiAnalysisError"));
      }
    );
  };

  const handleClose = () => {
    setSelectedFile(null);
    setAdditionalContext("");
    setAiResponse(null);
    setError(null);
    setIsLoading(false);
    onHide();
  };

  const getLevelBadgeVariant = (level) => {
    switch (level) {
      case "High":
      case "Yüksek":
        return "success";
      case "Medium":
      case "Orta":
        return "warning";
      case "Low":
      case "Düşük":
        return "secondary";
      default:
        return "info";
    }
  };

  const getProgressBarVariant = (score) => {
    if (score >= 70) return "success";
    if (score >= 40) return "warning";
    return "secondary";
  };

  const renderUploadSection = () => {
    const isAutoGenerated = autoUploadFile && selectedFile === autoUploadFile;

    return (
      <>
        {isAutoGenerated ? (
          <div className="mb-4">
            <div className="alert alert-success" role="alert">
              <strong>{t("pdfReady")}</strong>
              <br />
              <small>{selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)</small>
            </div>
          </div>
        ) : (
          <div className="mb-4">
            <p className="text-muted mb-3">{t("uploadPdfForAiAnalysis")}</p>
            <Form.Group>
              <Form.Label>{t("selectPdfFile")}</Form.Label>
              <Form.Control
                type="file"
                accept=".pdf"
                onChange={handleFileChange}
                disabled={isLoading}
              />
              {selectedFile && (
                <Form.Text className="text-success">
                  {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                </Form.Text>
              )}
            </Form.Group>
          </div>
        )}

        <Form.Group className="mb-3">
          <Form.Label>{t("additionalContext")} ({t("optional")})</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            value={additionalContext}
            onChange={(e) => setAdditionalContext(e.target.value)}
            placeholder={t("additionalContextPlaceholder")}
            disabled={isLoading}
          />
        </Form.Group>

        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}
      </>
    );
  };

  const renderLoadingSection = () => (
    <div className="text-center py-5">
      <div className="spinner-border text-primary mb-3" role="status">
        <span className="visually-hidden">{t("loading")}</span>
      </div>
      <p className="text-muted">{t("analyzingResults")}</p>
      <p className="text-muted small">{t("aiAnalysisMayTakeTime")}</p>
    </div>
  );

  const renderPersonalityTrait = (traitName, trait) => {
    if (!trait) return null;
    return (
      <div className="mb-3 p-3 border rounded">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <strong>{t(traitName)}</strong>
          <Badge bg={getLevelBadgeVariant(trait.level)}>{trait.level}</Badge>
        </div>
        {trait.score !== null && trait.score !== undefined && (
          <ProgressBar
            now={trait.score}
            label={`${trait.score}%`}
            variant={getProgressBarVariant(trait.score)}
            className="mb-2"
            style={{ height: "20px" }}
          />
        )}
        {trait.description && (
          <p className="mb-0 text-muted small">{trait.description}</p>
        )}
      </div>
    );
  };

  const renderTestResult = (testResult, index) => {
    if (!testResult) return null;
    return (
      <div key={index} className="mb-3 p-3 border rounded">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <strong>{testResult.testName}</strong>
          <Badge bg={getLevelBadgeVariant(testResult.level)}>{testResult.level}</Badge>
        </div>
        {testResult.score !== null && testResult.score !== undefined && (
          <ProgressBar
            now={testResult.score}
            label={`${testResult.score}%`}
            variant={getProgressBarVariant(testResult.score)}
            className="mb-2"
            style={{ height: "20px" }}
          />
        )}
        {testResult.description && (
          <p className="mb-0 text-muted small">{testResult.description}</p>
        )}
      </div>
    );
  };

  // Render results for personality inventory (Big5)
  const renderInventoryResultSection = () => (
    <div className="ai-analysis-result">
      {/* Summary */}
      <div className="mb-4 p-3 bg-light rounded">
        <h6 className="text-primary mb-2">{t("summary")}</h6>
        <p className="mb-0">{aiResponse.summary}</p>
      </div>

      {/* Personality Traits */}
      {aiResponse.personalityTraits && (
        <div className="mb-4">
          <h6 className="text-primary mb-3">{t("personalityTraits")}</h6>
          {renderPersonalityTrait("openness", aiResponse.personalityTraits.openness)}
          {renderPersonalityTrait("conscientiousness", aiResponse.personalityTraits.conscientiousness)}
          {renderPersonalityTrait("extraversion", aiResponse.personalityTraits.extraversion)}
          {renderPersonalityTrait("agreeableness", aiResponse.personalityTraits.agreeableness)}
          {renderPersonalityTrait("neuroticism", aiResponse.personalityTraits.neuroticism)}
        </div>
      )}

      {/* Skill Assessment */}
      {aiResponse.skillAssessment && (
        <div className="mb-4 p-3 border rounded">
          <h6 className="text-primary mb-2">{t("skillAssessment")}</h6>
          <p className="mb-0">{aiResponse.skillAssessment}</p>
        </div>
      )}

      {/* Strengths */}
      {aiResponse.strengths && aiResponse.strengths.length > 0 && (
        <div className="mb-4">
          <h6 className="text-primary mb-2">{t("strengths")}</h6>
          <ul className="mb-0">
            {aiResponse.strengths.map((strength, index) => (
              <li key={index} className="text-success">{strength}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Areas for Development */}
      {aiResponse.areasForDevelopment && aiResponse.areasForDevelopment.length > 0 && (
        <div className="mb-4">
          <h6 className="text-primary mb-2">{t("areasForDevelopment")}</h6>
          <ul className="mb-0">
            {aiResponse.areasForDevelopment.map((area, index) => (
              <li key={index} className="text-warning">{area}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Overall Recommendation */}
      {aiResponse.overallRecommendation && (
        <div className="p-3 bg-primary bg-opacity-10 rounded">
          <h6 className="text-primary mb-2">{t("overallRecommendation")}</h6>
          <p className="mb-0">{aiResponse.overallRecommendation}</p>
        </div>
      )}
    </div>
  );

  // Render results for skill/language tests
  const renderTestResultSection = () => (
    <div className="ai-analysis-result">
      {/* Summary */}
      <div className="mb-4 p-3 bg-light rounded">
        <h6 className="text-primary mb-2">{t("summary")}</h6>
        <p className="mb-0">{aiResponse.summary}</p>
      </div>

      {/* Test Results */}
      {aiResponse.testResults && aiResponse.testResults.length > 0 && (
        <div className="mb-4">
          <h6 className="text-primary mb-3">{t("testResults")}</h6>
          {aiResponse.testResults.map((testResult, index) => renderTestResult(testResult, index))}
        </div>
      )}

      {/* Strengths */}
      {aiResponse.strengths && aiResponse.strengths.length > 0 && (
        <div className="mb-4">
          <h6 className="text-primary mb-2">{t("strengths")}</h6>
          <ul className="mb-0">
            {aiResponse.strengths.map((strength, index) => (
              <li key={index} className="text-success">{strength}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Areas for Improvement */}
      {aiResponse.areasForImprovement && aiResponse.areasForImprovement.length > 0 && (
        <div className="mb-4">
          <h6 className="text-primary mb-2">{t("areasForImprovement")}</h6>
          <ul className="mb-0">
            {aiResponse.areasForImprovement.map((area, index) => (
              <li key={index} className="text-warning">{area}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Overall Recommendation */}
      {aiResponse.overallRecommendation && (
        <div className="p-3 bg-primary bg-opacity-10 rounded">
          <h6 className="text-primary mb-2">{t("overallRecommendation")}</h6>
          <p className="mb-0">{aiResponse.overallRecommendation}</p>
        </div>
      )}
    </div>
  );

  const renderResultSection = () => {
    if (analysisType === "test") {
      return renderTestResultSection();
    }
    return renderInventoryResultSection();
  };

  return (
    <Modal show={show} onHide={handleClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {aiResponse ? t("aiAnalysisResult") : t("askAi")}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
        {isLoading ? renderLoadingSection() : aiResponse ? renderResultSection() : renderUploadSection()}
      </Modal.Body>
      <Modal.Footer>
        {!aiResponse && !isLoading && (
          <Button variant="primary" onClick={handleSubmit} disabled={!selectedFile}>
            {t("analyze")}
          </Button>
        )}
        <Button variant="secondary" onClick={handleClose}>
          {t("close")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
